import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Label,
} from "reactstrap";
import CustomInput from "../Custom/CustomInput";
import CustomButton from "../Custom/CustomButton";

export interface Field {
  name: string;
  label: string;
  type?: "text" | "email" | "password" | "textarea" | "select" | "number";
  value?: string | number;
  placeholder?: string;
  required?: boolean;
  options?: { label: string; value: string | number }[];
  colSize?: 6 | 12; // 6 = two per row, 12 = full width
  rows?: number; // for textarea
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  email?: string;
  password?: string;
  isDisabled?: boolean;
  onSearch?: (searchTerm: string) => void;
  isLoadingOptions?: boolean;
  isPound?: boolean;
}

interface CustomFormModalProps {
  isOpen: boolean;
  title: string;
  fields: Field[];
  onCancel: () => void;
  submitText?: string;
  isLoading?: boolean;
  isEdit?: boolean;
  note?: string;
  children?: React.ReactNode;
  validation: any;
  isDisabled?: boolean;
}

const CustomFormModal: React.FC<CustomFormModalProps> = ({
  isOpen,
  title,
  fields,
  onCancel,
  submitText = "Save Details",
  isLoading = false,
  note,
  children,
  validation,
  isDisabled = false,
}) => {
  const handleCancel = () => {
    validation.resetForm();
    onCancel?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      size="md"
      backdrop="static"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          console.log(validation);
          validation.handleSubmit();
        }}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <ModalHeader toggle={handleCancel} className="bg-greyF5 py-3 ps-4 pe-4">
          <span className="text-black2D fs-16 fw-medium font-primary">
            {title}
          </span>
        </ModalHeader>

        <ModalBody className="pt-3 pb-0">
          <Row>
            {fields.map((field) => {
              const {
                name,
                label,
                type = "text",
                placeholder,
                required,
                options,
                colSize = 12,
                rows,
                disabled,
                isDisabled,
                isPound,
              } = field;

              const isSelect = type === "select";
              const isTextarea = type === "textarea";

              return (
                <Col
                  key={name}
                  xs={12}
                  md={colSize === 6 ? 6 : 12}
                  lg={colSize === 6 ? 6 : 12}
                >
                  {isTextarea ? (
                    <>
                      <Label className="font-primary fw-normal fs-14 text-black2D d-flex">
                        {label}{" "}
                        {required && <span className="text-danger">*</span>}
                      </Label>
                      <textarea
                        name={name}
                        className={`form-control ${
                          validation.touched[name] && validation.errors[name]
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder={placeholder}
                        rows={rows || 3}
                        disabled={disabled}
                        value={validation.values[name] || ""}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                      />
                      {validation.touched[name] && validation.errors[name] && (
                        <div className="invalid-feedback d-block">
                          {validation.errors[name]}
                        </div>
                      )}
                    </>
                  ) : (
                    <CustomInput
                      name={name}
                      label={label}
                      type={isSelect ? "select" : type}
                      placeholder={placeholder}
                      required={required}
                      dropdownOptions={options}
                      isPassword={type === "password"}
                      value={validation.values[name]}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      touched={validation.touched[name]}
                      error={validation.errors[name]}
                      isDisabled={isDisabled}
                      onSearch={field.onSearch} // Add this
                      isLoadingOptions={field.isLoadingOptions} // Add this
                      isPound={isPound}
                    />
                  )}
                </Col>
              );
            })}
          </Row>
          {note && (
            <p className="text-grey89 fs-14 font-primary">
              <span className="fw-medium ">NOTE :</span> {note}
            </p>
          )}

          {children}
        </ModalBody>

        <ModalFooter className="justify-content-between mt-0">
          <CustomButton
            text={submitText}
            type="submit"
            loading={isLoading}
            disabled={isLoading}
            className={`bg-primary br-7 ${title === "Support" ? "mt-3" : ""}`}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CustomFormModal;
